import { Colors, Pills, Tabs, Text } from '@amelio/ui-library';

import { COMPENSATION_LEVEL_TILES_LAYOUT } from '../compensationStyles';
import { LevelSummaryTable } from './LevelSummaryTable';
import { LevelTile } from './LevelTile';
import { ScoreTiles } from './ScoreTiles';

import type {
    CompensationComputedView,
    CompensationDistributionMode,
    CompensationLevel,
} from '@/types/compensation';

import { useAmelioTranslations } from '@/hooks';

interface StrategyCardProps {
    distributionMode: CompensationDistributionMode;
    levelMultipliers: Record<string, number>;
    scoreThreshold: number;
    scoreSpread: number;
    levels: CompensationLevel[];
    computed: CompensationComputedView | undefined;
    canEdit: boolean;
    onDistributionModeChange: (mode: CompensationDistributionMode) => void;
    onLevelMultiplierChange: (levelId: string, value: number) => void;
    onScoreThresholdChange: (value: number) => void;
    onScoreSpreadChange: (value: number) => void;
}

/**
 * Section 2, right card: how the budget is spread. Either a multiplier per performance level, or a
 * curve over the score itself — and, under both, the same per-level summary, so switching mode is
 * a comparison rather than a leap of faith.
 */
export const StrategyCard = ({
    distributionMode,
    levelMultipliers,
    scoreThreshold,
    scoreSpread,
    levels,
    computed,
    canEdit,
    onDistributionModeChange,
    onLevelMultiplierChange,
    onScoreThresholdChange,
    onScoreSpreadChange,
}: StrategyCardProps) => {
    const { t } = useAmelioTranslations();

    const isLevelMode = distributionMode === 'Level';
    const modeLabelKey = isLevelMode
        ? 'compensation.strategyCard.modeLevel'
        : 'compensation.strategyCard.modeScore';

    const summaryByLevelId = new Map(
        (computed?.levelSummary ?? []).map((entry) => [entry.levelId, entry]),
    );

    // In `Level` mode every eligible person of a level is proposed the same percentage, so a tile
    // reads it off one of its computed lines instead of reimplementing the distribution.
    const proposedPercentByLevelId = new Map<string, number>();
    (computed?.lines ?? []).forEach((line) => {
        if (line.performanceLevelId && line.hasScore && line.hasSalary) {
            proposedPercentByLevelId.set(line.performanceLevelId, line.proposedPercent);
        }
    });

    return (
        <div
            className={'flex flex-col gap-[16px]'}
            data-testid={'compensation-strategy-card'}
        >
            {/* A closed envelope refuses the change the tabs would dispatch, so it is told which
                strategy is in force instead of being offered two tabs that raise the save bar and
                then fail on a conflict. */}
            <div className={'flex justify-end'}>
                {canEdit ? (
                    <Tabs
                        variant={'segmented'}
                        activeKey={distributionMode}
                        onChange={(key) =>
                            onDistributionModeChange(key as CompensationDistributionMode)
                        }
                        items={[
                            {
                                key: 'Level',
                                label: t('compensation.strategyCard.modeLevel'),
                                dataTestId: 'compensation-strategy-mode-level',
                            },
                            {
                                key: 'Score',
                                label: t('compensation.strategyCard.modeScore'),
                                dataTestId: 'compensation-strategy-mode-score',
                            },
                        ]}
                        dataTestId={'compensation-strategy-mode'}
                    />
                ) : (
                    <Pills
                        size={'sm'}
                        dataTestId={'compensation-strategy-mode-static'}
                    >
                        {t(modeLabelKey)}
                    </Pills>
                )}
            </div>

            <Text color={Colors.BASE_500}>
                {t(
                    isLevelMode
                        ? 'compensation.strategyCard.modeLevelDescription'
                        : 'compensation.strategyCard.modeScoreDescription',
                )}
            </Text>

            {isLevelMode ? (
                <div className={COMPENSATION_LEVEL_TILES_LAYOUT}>
                    {levels.map((level) => (
                        <LevelTile
                            key={level.id}
                            level={level}
                            multiplier={levelMultipliers[level.id] ?? level.defaultMultiplier}
                            employeeCount={summaryByLevelId.get(level.id)?.employeeCount ?? 0}
                            shareOfSalary={proposedPercentByLevelId.get(level.id) ?? null}
                            canEdit={canEdit}
                            onMultiplierChange={(value) => onLevelMultiplierChange(level.id, value)}
                        />
                    ))}
                </div>
            ) : (
                <ScoreTiles
                    scoreThreshold={scoreThreshold}
                    scoreSpread={scoreSpread}
                    excludedByThresholdCount={computed?.excludedByThresholdCount ?? 0}
                    spreadRatio={computed?.spreadRatio ?? null}
                    canEdit={canEdit}
                    onThresholdChange={onScoreThresholdChange}
                    onSpreadChange={onScoreSpreadChange}
                />
            )}

            <LevelSummaryTable
                levels={levels}
                levelSummary={computed?.levelSummary ?? []}
            />
        </div>
    );
};
